import React, { createContext, PropsWithChildren, ReactNode, useContext } from 'react';
import { createPortal } from 'react-dom';
import { v4 as uuid } from 'uuid';

import styles from './ToastProvider.module.scss';

import { ToastController } from './ToastController';

interface ToastContextProps {
  /**
   * Show Toast
   *
   * @param options use options as properties for Toast as it's defined in the styleguide
   * http://hse-storybook2.azurewebsites.net/?path=/story/core-components-toastlayout-toast--default
   *
   * @returns unique id for the toast instance, can be use to remove a Toast
   */
  add: (options: ToastOptions) => string;
  showInfo: (title: string, children?: ReactNode) => string;
  showSuccess: (title: string, children?: ReactNode) => string;
  showWarning: (title: string, children?: ReactNode) => string;
  showError: (title: string, children?: ReactNode) => string;
  remove: (id: string) => void;
  removeAll: () => void;
}
const ToastContext = createContext<ToastContextProps>(null);
const { Provider } = ToastContext;

export interface ToastOptions {
  id?: string;
  title: string;
  [key: string]: any;
}

interface Props {
  domTarget?: Element;
}

interface State {
  toasts: ToastOptions[];
}

export class ToastProvider extends React.Component<PropsWithChildren<Props>, State> {
  state = { toasts: [] };

  exists = (id: string) => {
    return this.state.toasts.some((t) => t.id === id);
  };

  showError = (title: string, children?: ReactNode) => {
    return this.add({
      title,
      children,
      type: 'negative', // 'default' | 'positive' | 'negative' | 'warning'
      timeout: 10,
      onClose: () => {},
    });
  };

  showInfo = (title: string, children?: ReactNode) => {
    return this.add({
      title,
      children,
      type: 'default', // 'default' | 'positive' | 'negative' | 'warning'
      timeout: 10,
      onClose: () => {},
    });
  };

  showSuccess = (title: string, children?: ReactNode) => {
    return this.add({
      title,
      children,
      type: 'positive', // 'default' | 'positive' | 'negative' | 'warning'
      timeout: 10,
      onClose: () => {},
    });
  };

  showWarning = (title: string, children?: ReactNode) => {
    return this.add({
      title,
      children,
      type: 'warning',
      timeout: 10,
      onClose: () => {},
    });
  };

  add = (options: ToastOptions) => {
    const { id = uuid(), timeout = 10 } = options;
    if (this.exists(id)) {
      return id;
    }
    this.setState((state) => {
      const toastToAdd = { id, timeout, ...options };
      const toasts = [...state.toasts, toastToAdd];
      return { toasts };
    });
    return id;
  };

  remove = (id: string) => {
    if (!this.exists(id)) {
      return;
    }
    this.setState((state) => {
      const toasts = state.toasts.filter((t) => t.id !== id);
      return { toasts };
    });
  };

  removeAll = () => {
    this.setState(() => {
      return { toasts: [] };
    });
  };

  handleClose = (id: string) => () => {
    this.remove(id);
  };

  render() {
    const { domTarget = document.body, children } = this.props;
    const { add, showError, remove, showWarning, removeAll, showSuccess, showInfo } = this;
    const toasts = Object.freeze(this.state.toasts);
    if (!domTarget) {
      return <>{children}</>;
    }
    return (
      <Provider value={{ add, showError, showWarning, showSuccess, showInfo, remove, removeAll }}>
        {children}
        {createPortal(
          <div className={styles.toastContainer}>
            {toasts.map(({ id, ...rest }) => (
              <ToastController
                key={id}
                onDismiss={this.handleClose(id)}
                classNames={[styles.toast]}
                {...rest}
              />
            ))}
          </div>,
          domTarget,
        )}
      </Provider>
    );
  }
}

export const useToasts = () => {
  const ctx = useContext(ToastContext);

  if (!ctx) {
    throw Error('Please configure ToastContext before using "useToasts()"');
  }

  return {
    addToast: ctx.add,
    showErrorToast: ctx.showError,
    showWarning: ctx.showWarning,
    showInfo: ctx.showInfo,
    showSuccess: ctx.showSuccess,
    removeToast: ctx.remove,
    removeAllToasts: ctx.removeAll,
  };
};
